import React, { useState } from 'react';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaClock, FaQuestionCircle } from "react-icons/fa";
import LoadingSpinner from '../components/LoadingSpinner';
import '../styles/ContactUs.css';
import { API } from "../common/Constants";
import { API_BASE_URL } from "../environment";
import { analyticsService } from "../services/analyticsService";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [formStatus, setFormStatus] = useState({
    submitting: false,
    submitted: false,
    error: false,
    errorMessage: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({
      submitting: true,
      submitted: false,
      error: false,
      errorMessage: "",
    });

    try {
      const response = await fetch(`${API_BASE_URL}${API.CONTACT_FORM}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to send message");
      }

      // Track successful contact form submission
      analyticsService.trackEvent(
        'Contact',
        'Form Submission',
        `Subject: ${formData.subject}`,
        1
      );

      setFormStatus({
        submitting: false,
        submitted: true,
        error: false,
        errorMessage: "",
      });
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      console.error("Error sending contact form:", error);
      setFormStatus({
        submitting: false,
        submitted: false,
        error: true,
        errorMessage:
          error.message || "Failed to send message. Please try again.",
      });
    }
  };

  if (formStatus.submitting) {
    return <LoadingSpinner />;
  }

  return (
    <div className="contact-us-container">
      <h1 className="contact-us-main-heading">Contact Us</h1>
      
      <div className="contact-us-content">
        <div className="contact-us-section">
          <h2>Get in Touch</h2>
          <p>
            We're here to help you with any questions or concerns about Pathshala Sanchalak. 
            Whether you need technical support, want to request a demo, or have feedback about our 
            school and coaching management system, we'd love to hear from you.
          </p>
        </div>

        <div className="contact-us-section">
          <h2>Contact Information</h2>
          <div className="contact-info-grid">
            <div className="contact-info-item">
              <div className="contact-info-icon">
                <FaEnvelope />
              </div>
              <div className="contact-info-text">
                <h3>Email Address</h3>
                <p>pathshalasanchalak@gmail.com</p>
                <span>Primary contact for all inquiries</span>
              </div>
            </div>

            <div className="contact-info-item">
              <div className="contact-info-icon">
                <FaPhone />
              </div>
              <div className="contact-info-text">
                <h3>Phone Number</h3>
                <p>+91 7457804496</p>
                <span>Available during business hours</span>
              </div>
            </div>

            <div className="contact-info-item">
              <div className="contact-info-icon">
                <FaMapMarkerAlt />
              </div>
              <div className="contact-info-text">
                <h3>Office Address</h3>
                <p>291/4 Jagriti Vihar<br />Meerut, Uttar Pradesh<br />India</p>
                <span>Visit us during office hours</span>
              </div>
            </div>

            <div className="contact-info-item">
              <div className="contact-info-icon">
                <FaClock />
              </div>
              <div className="contact-info-text">
                <h3>Business Hours</h3>
                <p>Monday - Friday: 9:00 AM - 6:00 PM<br />Saturday: 10:00 AM - 4:00 PM</p>
                <span>Indian Standard Time (IST)</span>
              </div>
            </div>
          </div>
        </div>

        <div className="contact-us-section">
          <h2>Send us a Message</h2>
          <p>
            Fill out the form below and we'll get back to you as soon as possible. 
            Please provide as much detail as possible so we can assist you better.
          </p>

          {formStatus.submitted ? (
            <div className="success-message">
              <div className="success-icon">✓</div>
              <h3>Message Sent Successfully!</h3>
              <p>Thank you for contacting us. We've received your message and will get back to you within 24 hours.</p>
              <button
                className="btn btn-primary"
                onClick={() =>
                  setFormStatus({
                    submitting: false,
                    submitted: false,
                    error: false,
                  })
                }
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form className="contact-form" onSubmit={handleSubmit}>
              {formStatus.error && (
                <div className="error-alert">
                  <p>
                    {formStatus.errorMessage ||
                      "Failed to send message. Please try again."}
                  </p>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Your Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Your Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email address"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject *</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="What is this regarding?"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  rows="6"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Please describe your inquiry in detail..."
                  required
                ></textarea>
              </div>

              <button
                type="submit"
                className="btn btn-primary submit-btn"
                disabled={formStatus.submitting}
              >
                {formStatus.submitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          )}
        </div>

        <div className="contact-us-section">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <div className="faq-icon">
                <FaQuestionCircle />
              </div>
              <div className="faq-content">
                <h3>How do I get started with Pathshala Sanchalak?</h3>
                <p>Simply register for a free account on our platform. You can start using our school management system immediately after verification.</p>
              </div>
            </div>

            <div className="faq-item">
              <div className="faq-icon">
                <FaQuestionCircle />
              </div>
              <div className="faq-content">
                <h3>Is technical support included?</h3>
                <p>Yes! We provide comprehensive technical support via email and phone. Our team is here to help you make the most of our platform.</p>
              </div>
            </div>

            <div className="faq-item">
              <div className="faq-icon">
                <FaQuestionCircle />
              </div>
              <div className="faq-content">
                <h3>Can I request a demo?</h3>
                <p>Absolutely! Contact us to schedule a personalized demo of our school or coaching management system features.</p>
              </div>
            </div>

            <div className="faq-item">
              <div className="faq-icon">
                <FaQuestionCircle />
              </div>
              <div className="faq-content">
                <h3>How quickly do you respond to inquiries?</h3>
                <p>We typically respond to all inquiries within 24 hours during business days, often much sooner for urgent matters.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="contact-us-section">
          <h2>Additional Resources</h2>
          <div className="resources-grid">
            <div className="resource-item">
              <h3>User Guide</h3>
              <p>Comprehensive documentation to help you navigate and use all features effectively.</p>
              <a href="/user-guide" className="resource-link">View User Guide →</a>
            </div>
            
            <div className="resource-item">
              <h3>Privacy Policy</h3>
              <p>Learn about how we protect and handle your data and privacy.</p>
              <a href="/privacy-policy" className="resource-link">Read Privacy Policy →</a>
            </div>

            <div className="resource-item">
              <h3>Terms & Conditions</h3>
              <p>Review our terms of service and usage policies.</p>
              <a href="/terms-and-conditions" className="resource-link">Read Terms →</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;